import * as borogove from "borogove";
import { Button } from "@chatscope/chat-ui-kit-react";
import { Suspense, useRef, useState } from "react";
import { useAsyncValue, Await } from "react-router";
import { CircularProgress } from "@mui/material";

import { FormSection } from "./Form";
import { ios, webkit } from "../util";

const CommandSessionInner = ({
	updateSession,
	logStep,
}: {
	updateSession: (
		session: Promise<borogove.CommandSession> | undefined,
	) => void;
	logStep: (form: borogove.Form, data: FormData) => void;
}) => {
	const session = useAsyncValue() as borogove.CommandSession;
	const formRef = useRef<HTMLFormElement>(null);
	const form = session.forms[0];

	if (!form && ["completed", "canceled"].includes(session.status)) {
		updateSession(undefined);
		return <CircularProgress />;
	}

	if (!form) {
		return (
			<div className="command-session">
				<p>{session.status}</p>
			</div>
		);
	}

	const execute = (action: string | null) => {
		const data =
			formRef.current && !form.isResult()
				? new FormData(formRef.current)
				: null;
		updateSession(session.execute(action, data));
		if (action !== "cancel") logStep(form, data ?? new FormData());
	};

	const commandActions = () => {
		return (
			<div className="command-actions">
				{session.actions.toReversed().map((action) => (
					<Button
						type={
							action.value === session.actions[0]?.value ? "submit" : "button"
						}
						key={action.value}
						onClick={() => execute(action.value)}
					>
						{action.label}
					</Button>
				))}
				{session.status !== "executing" && (
					<Button onClick={() => updateSession(undefined)}>Close</Button>
				)}
			</div>
		);
	};

	return (
		<div className="command-session">
			{form.url() ? (
				<>
					{ios || webkit ? (
						<a target="_blank" rel="opener" href={form.url() ?? undefined}>
							{form.title() ?? "click here"}
						</a>
					) : (
						<iframe
							src={form.url() ?? undefined}
							allow="microphone *; camera *"
						></iframe>
					)}
					{commandActions()}
				</>
			) : (
				<form method="dialog" ref={formRef}>
					<FormSection section={form} isResult={form.isResult()} />
					{commandActions()}
				</form>
			)}
		</div>
	);
};

export const CommandSession = ({
	session,
	idx,
	updateSession,
	logStep,
}: {
	session: Promise<borogove.CommandSession>;
	idx: number;
	updateSession: (
		idx: number,
		session: Promise<borogove.CommandSession> | undefined,
	) => void;
	logStep?: (form: borogove.Form, data: FormData) => void;
}) => {
	const [generation, setGeneration] = useState(idx);
	const update = (session: Promise<borogove.CommandSession> | undefined) => {
		setGeneration((g) => g + 1);
		updateSession(idx, session);
	};
	return (
		<Suspense key={generation} fallback={<CircularProgress />}>
			<Await resolve={session}>
				<CommandSessionInner
					updateSession={update}
					logStep={logStep ?? (() => {})}
				/>
			</Await>
		</Suspense>
	);
};
